import React, { useState, useEffect } from 'react';
import {
  Modal,
  Text,
  BlockStack,
  TextField,
  Select,
  Checkbox,
  Autocomplete,
  Icon,
  InlineStack,
  Button,
  Banner,
} from '@shopify/polaris';
import {
  SearchMinor,
} from '@shopify/polaris-icons';
import { generationScenesService } from '../../services/generationScenesService';
import { generationImportService } from '../../services/generationImportService';
import TextStyleEditor from '../text/TextStyleEditor';
import {
  buildTextStylePayload,
  normalizeTextStyle,
  toOptionalNumber,
} from '../../utils/textStyleLayout';

const FONT_STYLE_OPTIONS = [
  { label: 'Regular', value: 'regular' },
  { label: 'Italic', value: 'italic' },
  { label: 'Bold', value: 'bold' },
  { label: 'Bold Italic', value: 'bold_italic' },
];

const TEXT_ALIGN_OPTIONS = [
  { label: 'Left', value: 'left' },
  { label: 'Center', value: 'center' },
  { label: 'Right', value: 'right' },
];

const fileInputStyle = {
  width: '100%',
  border: '1px solid #c9cccf',
  borderRadius: 8,
  padding: '8px 10px',
  fontSize: 13,
};

const CreateGenerationSceneModal = ({ 
  isOpen, 
  onClose, 
  onSceneCreated,
  generationStories,
  isLoadingGenerationStories
}) => {
  const [isCreatingGenerationScene, setIsCreatingGenerationScene] = useState(false);
  const [generationStoryAutocompleteInputValue, setGenerationStoryAutocompleteInputValue] = useState('');
  const [generationStoryAutocompleteOptions, setGenerationStoryAutocompleteOptions] = useState([]);
  const [selectedGenerationStoryForScene, setSelectedGenerationStoryForScene] = useState([]);
  const [availableFonts, setAvailableFonts] = useState([]);
  const [isLoadingFonts, setIsLoadingFonts] = useState(false);
  const [sceneAssetFile, setSceneAssetFile] = useState(null);
  const [maskAssetFile, setMaskAssetFile] = useState(null);
  const [isUploadingSceneAsset, setIsUploadingSceneAsset] = useState(false);
  const [isUploadingMaskAsset, setIsUploadingMaskAsset] = useState(false);
  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');

  const [newGenerationSceneData, setNewGenerationSceneData] = useState({
    slug: '',
    sceneTitle: '',
    order: '',
    type: '',
    scene_url: '',
    mask_url: '',
    text: '',
    text_overlay_enabled: false,
    scene_prompt: '',
    left_text: '',
    right_text: '',
    cover_wrap: false,
    blurred: false,
    story_id: '',
    demo_text: '',
    use_default_text_style: true,
    text_style_form: normalizeTextStyle({}),
    use_default_cover_style: true,
    cover_font_key: '',
    cover_font_size: '',
    cover_font_style: 'regular',
    cover_text_align: 'center',
    cover_text_x: '',
    cover_text_y: '',
    cover_font_color: '#ffffff',
    cover_rotation: ''
  });

  const fontOptions = [
    { label: 'Language fallback font', value: '' },
    ...availableFonts.map((font) => ({
      label: `${font.label || font.family || font.key} (${font.key})`,
      value: font.key || ''
    }))
  ];

  // Helper function to generate slug from title
  const generateSlug = (title) => {
    if (!title) return '';
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  // Helper function to determine which fields should be shown based on scene type
  const getSceneTypeFields = (sceneType) => {
    switch (sceneType) {
      case 'cover':
        return {
          showText: true,
          showMaskUrl: true,
          showBlurred: false,
          showDemoText: true,
          showPrompt: true,
          textRequired: false,
          maskUrlRequired: false,
          demoTextRequired: false
        };
      case 'image':
        return {
          showText: false,
          showMaskUrl: true,
          showBlurred: false,
          showDemoText: false,
          showPrompt: true,
          textRequired: false,
          maskUrlRequired: false,
          demoTextRequired: false
        };
      case 'text':
        return {
          showText: true,
          showMaskUrl: false,
          showBlurred: false,
          showDemoText: false,
          showPrompt: false,
          textRequired: true,
          maskUrlRequired: false,
          demoTextRequired: false
        };
      case 'static':
        return {
          showText: false,
          showMaskUrl: false,
          showBlurred: true,
          showDemoText: false,
          showPrompt: false,
          textRequired: false,
          maskUrlRequired: false,
          demoTextRequired: false
        };
      case 'back':
        return {
          showText: false,
          showMaskUrl: false,
          showBlurred: false,
          showDemoText: false,
          showPrompt: false,
          textRequired: false,
          maskUrlRequired: false,
          demoTextRequired: false
        };
      default:
        return {
          showText: false,
          showMaskUrl: false,
          showBlurred: false,
          showDemoText: false,
          showPrompt: false,
          textRequired: false,
          maskUrlRequired: false,
          demoTextRequired: false
        };
    }
  };

  // Helper function to prepare scene data based on scene type
  const prepareSceneData = (sceneData) => {
    const textStyleOverride = ['text', 'image'].includes(sceneData.type)
      ? (
        sceneData.use_default_text_style
          ? { use_default: true }
          : buildTextStylePayload(sceneData.text_style_form, { useDefault: false })
      )
      : null;

    const coverStyleOverride = sceneData.type === 'cover'
      ? (
        sceneData.use_default_cover_style
          ? { use_default: true }
          : {
            use_default: false,
            font_key: sceneData.cover_font_key || '',
            font_size: toOptionalNumber(sceneData.cover_font_size),
            font_style: sceneData.cover_font_style || 'regular',
            text_align: sceneData.cover_text_align || 'center',
            x: toOptionalNumber(sceneData.cover_text_x),
            y: toOptionalNumber(sceneData.cover_text_y),
            color: sceneData.cover_font_color || '#ffffff',
            rotation: toOptionalNumber(sceneData.cover_rotation) ?? 0,
          }
      )
      : null;

    const baseData = {
      slug: sceneData.slug,
      order: sceneData.order,
      type: sceneData.type,
      scene_url: sceneData.scene_url || '',
      story_id: sceneData.story_id,
      text_style_override: textStyleOverride,
      cover_style_override: coverStyleOverride,
    };

    switch (sceneData.type) {
      case 'cover':
        return {
          ...baseData,
          mask_url: sceneData.mask_url,
          text: sceneData.text,
          demo_text: sceneData.demo_text,
          scene_prompt: sceneData.scene_prompt,
          ...(isSpreadStory ? { cover_wrap: sceneData.cover_wrap === true && String(sceneData.order) === '0' } : {})
        };
      case 'image':
        return {
          ...baseData,
          mask_url: sceneData.mask_url,
          text_overlay_enabled: sceneData.text_overlay_enabled === true,
          text: String(sceneData.text || ''),
          scene_prompt: sceneData.scene_prompt,
          ...(isSpreadStory ? {
            left_text: String(sceneData.left_text || ''),
            right_text: String(sceneData.right_text || '')
          } : {})
        };
      case 'text':
        return {
          ...baseData,
          text: sceneData.text
        };
      case 'static':
        return {
          ...baseData,
          blurred: sceneData.blurred
        };
      case 'back':
        return {
          ...baseData
        };
      default:
        return baseData;
    }
  };

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setNewGenerationSceneData({
        slug: '', sceneTitle: '', order: '', type: '', scene_url: '', mask_url: '', text: '', text_overlay_enabled: false, scene_prompt: '', left_text: '', right_text: '', cover_wrap: false, blurred: false, story_id: '', demo_text: '', use_default_text_style: true, text_style_form: normalizeTextStyle({}), use_default_cover_style: true, cover_font_key: '', cover_font_size: '', cover_font_style: 'regular', cover_text_align: 'center', cover_text_x: '', cover_text_y: '', cover_font_color: '#ffffff', cover_rotation: ''
      });
      setSelectedGenerationStoryForScene([]);
      setGenerationStoryAutocompleteInputValue('');
      setSceneAssetFile(null);
      setMaskAssetFile(null);
      setActionError('');
      setActionSuccess('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    let isMounted = true;
    const loadFonts = async () => {
      setIsLoadingFonts(true);
      try {
        const result = await generationImportService.getFonts({ activeOnly: true });
        if (isMounted) {
          setAvailableFonts(Array.isArray(result?.data) ? result.data : []);
        }
      } catch (err) {
        if (isMounted) {
          setAvailableFonts([]);
        }
      } finally {
        if (isMounted) {
          setIsLoadingFonts(false);
        }
      }
    };

    loadFonts();
    return () => {
      isMounted = false;
    };
  }, [isOpen]);

  // Update autocomplete options when stories change
  useEffect(() => {
    const options = generationStories.map(story => ({ label: story.title || '', value: story._id }));
    setGenerationStoryAutocompleteOptions(options);
  }, [generationStories]);

  // Generation Scene autocomplete handlers
  const handleGenerationStoryAutocompleteChange = (value) => {
    setGenerationStoryAutocompleteInputValue(value);
    if (value === '') {
      const allOptions = generationStories.map(story => ({ label: story.title || '', value: story._id }));
      setGenerationStoryAutocompleteOptions(allOptions);
      return;
    }
    const filterRegex = new RegExp(value, 'i');
    const filteredOptions = generationStories
      .filter(story => (story.title && story.title.match(filterRegex)) || (story.slug && story.slug.match(filterRegex)))
      .map(story => ({ label: story.title || '', value: story._id }));
    setGenerationStoryAutocompleteOptions(filteredOptions);
  };

  const handleGenerationStoryAutocompleteSelect = (selected) => {
    setSelectedGenerationStoryForScene(selected);
    const selectedId = selected[0] || '';
    setNewGenerationSceneData(prev => ({ ...prev, story_id: selectedId }));
    const selectedStory = generationStories.find(story => story._id === selectedId);
    setGenerationStoryAutocompleteInputValue(selectedStory ? selectedStory.title || '' : '');
  };

  const selectedGenerationStory = generationStories.find(
    (story) => story._id === newGenerationSceneData.story_id
  );
  const isPromptStory = selectedGenerationStory?.comfy_flow === 'prompt_story';
  const isSpreadStory = selectedGenerationStory?.layout_mode === 'spread';

  const getCurrentStorySlug = () => {
    const story = generationStories?.find(
      (candidate) => candidate._id === newGenerationSceneData.story_id
    );
    return story?.slug || '';
  };

  // Uploads a selected file and returns its URL. The caller stores the URL on the scene.
  const uploadAssetFile = async (assetKind, file) => {
    const isMask = assetKind === 'mask';
    const setUploading = isMask ? setIsUploadingMaskAsset : setIsUploadingSceneAsset;
    setUploading(true);
    try {
      const result = await generationScenesService.uploadSceneAsset({
        file,
        assetKind,
        storySlug: getCurrentStorySlug(),
        sceneSlug: newGenerationSceneData.slug,
        order: newGenerationSceneData.order,
      });
      const uploadedUrl = result?.data?.url || '';
      if (!uploadedUrl) {
        throw new Error(`Uploaded ${assetKind} did not return a URL`);
      }
      return uploadedUrl;
    } finally {
      setUploading(false);
    }
  };

  const handleCreateGenerationScene = async () => {
    const hasPrompt = Boolean(String(newGenerationSceneData.scene_prompt || '').trim());
    const hasSceneUrl = Boolean(String(newGenerationSceneData.scene_url || '').trim()) || Boolean(sceneAssetFile);
    const hasMaskUrl = Boolean(String(newGenerationSceneData.mask_url || '').trim()) || Boolean(maskAssetFile);

    if (!newGenerationSceneData.slug || !newGenerationSceneData.type || !newGenerationSceneData.story_id) {
      return;
    }

    if (isPromptStory && newGenerationSceneData.type !== 'back') {
      if (!hasPrompt) {
        return;
      }
    } else if (newGenerationSceneData.type === 'back') {
      if (!hasSceneUrl) return;
    } else if (!hasSceneUrl && !hasPrompt) {
      return;
    }
    
    const typeFields = getSceneTypeFields(newGenerationSceneData.type);
    if (typeFields.textRequired && !newGenerationSceneData.text) return;
    if (newGenerationSceneData.type === 'image' && newGenerationSceneData.text_overlay_enabled) {
      const hasOverlayText = isSpreadStory
        ? Boolean(String(newGenerationSceneData.left_text || '').trim() || String(newGenerationSceneData.right_text || '').trim())
        : Boolean(String(newGenerationSceneData.text || '').trim());
      if (!hasOverlayText) return;
    }
    if (typeFields.maskUrlRequired && !isPromptStory && !hasMaskUrl && !hasPrompt) return;
    if (typeFields.demoTextRequired && !newGenerationSceneData.demo_text) return;

    setIsCreatingGenerationScene(true);
    setActionError('');
    setActionSuccess('');
    try {
      // Upload any selected files first, so the scene is saved with their URLs.
      const [uploadedSceneUrl, uploadedMaskUrl] = await Promise.all([
        !newGenerationSceneData.scene_url && sceneAssetFile
          ? uploadAssetFile('scene', sceneAssetFile)
          : Promise.resolve(newGenerationSceneData.scene_url),
        !newGenerationSceneData.mask_url && maskAssetFile
          ? uploadAssetFile('mask', maskAssetFile)
          : Promise.resolve(newGenerationSceneData.mask_url),
      ]);
      const preparedData = prepareSceneData({
        ...newGenerationSceneData,
        scene_url: uploadedSceneUrl || '',
        mask_url: uploadedMaskUrl || '',
      });
      await generationScenesService.createScene(preparedData);
      
      // Reset form
      setNewGenerationSceneData({
        slug: '', sceneTitle: '', order: '', type: '', scene_url: '', mask_url: '', text: '', text_overlay_enabled: false, scene_prompt: '', left_text: '', right_text: '', cover_wrap: false, blurred: false, story_id: '', demo_text: '', use_default_text_style: true, text_style_form: normalizeTextStyle({}), use_default_cover_style: true, cover_font_key: '', cover_font_size: '', cover_font_style: 'regular', cover_text_align: 'center', cover_text_x: '', cover_text_y: '', cover_font_color: '#ffffff', cover_rotation: ''
      });
      setSelectedGenerationStoryForScene([]);
      setGenerationStoryAutocompleteInputValue('');
      setSceneAssetFile(null);
      setMaskAssetFile(null);
      
      onClose();
      
      // Notify parent component to refresh scenes list
      if (onSceneCreated) {
        onSceneCreated();
      }
    } catch (err) {
      console.error('Error creating generation scene:', err);
      setActionError(err?.message || 'Failed to create generation scene');
    } finally {
      setIsCreatingGenerationScene(false);
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title={<div style={{padding: '16px'}}>{'Create New Generation Scene'}</div>}
      primaryAction={{
        content: 'Create',
        onAction: handleCreateGenerationScene,
        loading: isCreatingGenerationScene,
        disabled: isUploadingSceneAsset || isUploadingMaskAsset,
      }}
      secondaryActions={[
        {
          content: 'Cancel',
          onAction: onClose,
        },
      ]}
      footer={<div style={{padding: '16px'}}></div>}
    >
      <BlockStack gap="400">
        {actionError ? (
          <Banner tone="critical" onDismiss={() => setActionError('')}>
            <Text as="p">{actionError}</Text>
          </Banner>
        ) : null}
        {actionSuccess ? (
          <Banner tone="success" onDismiss={() => setActionSuccess('')}>
            <Text as="p">{actionSuccess}</Text>
          </Banner>
        ) : null}
        <TextField
          label="Scene Title"
          value={newGenerationSceneData.sceneTitle}
          onChange={(value) => setNewGenerationSceneData(prev => ({ ...prev, sceneTitle: value, slug: generateSlug(value) }))}
          placeholder="Scene title"
          required
        />
        <TextField
          label="Scene Slug"
          value={newGenerationSceneData.slug}
          onChange={() => {}}
          placeholder="scene-slug"
          required
          disabled
        />
        <TextField
          label="Order"
          type="number"
          value={newGenerationSceneData.order}
          onChange={(value) => setNewGenerationSceneData(prev => ({ ...prev, order: parseInt(value) || 0 }))}
          placeholder="0"
          min="0"
          required
        />
        <Select
          label="Scene Type"
          options={[
            { label: 'Select Scene Type', value: '', disabled: true },
            { label: 'Cover', value: 'cover' },
            { label: 'Image', value: 'image' },
            { label: 'Text', value: 'text' },
            { label: 'Static', value: 'static' },
            { label: 'Back', value: 'back' },
          ]}
          onChange={(value) => setNewGenerationSceneData(prev => ({ ...prev, type: value }))}
          value={newGenerationSceneData.type}
          required
        />
        <Autocomplete
          options={generationStoryAutocompleteOptions}
          selected={selectedGenerationStoryForScene}
          onSelect={handleGenerationStoryAutocompleteSelect}
          loading={isLoadingGenerationStories}
          textField={
            <Autocomplete.TextField
              onChange={handleGenerationStoryAutocompleteChange}
              label="Story"
              value={generationStoryAutocompleteInputValue}
              prefix={<Icon source={SearchMinor} tone="base" />}
              placeholder="Search for a story..."
              autoComplete="off"
              required
            />
          }
        />
        {(!isPromptStory || newGenerationSceneData.type === 'back') && (
          <TextField
            label="Scene URL (or upload below)"
            value={newGenerationSceneData.scene_url}
            onChange={(value) => {
              setNewGenerationSceneData(prev => ({ ...prev, scene_url: value }));
              if (value) setSceneAssetFile(null);
            }}
            placeholder="https://example.com/scene.png"
            required={(!isPromptStory || newGenerationSceneData.type === 'back') && !sceneAssetFile}
          />
        )}
        {(!isPromptStory || newGenerationSceneData.type === 'back') && (
          <div style={{ border: '1px solid #e3e3e3', borderRadius: 8, padding: 12 }}>
            <BlockStack gap="200">
              <Text as="p" variant="bodySm" tone="subdued">
                Upload a scene image instead of entering a URL. It will also upload automatically when you create the scene.
              </Text>
              <label htmlFor="create-generation-scene-image">Scene image file</label>
              <input
                id="create-generation-scene-image"
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={(event) => {
                  const file = event.target.files?.[0] || null;
                  setSceneAssetFile(file);
                  if (file) {
                    setNewGenerationSceneData((previous) => ({ ...previous, scene_url: '' }));
                  }
                  setActionError('');
                  setActionSuccess('');
                }}
                style={fileInputStyle}
              />
              <InlineStack gap="200" align="space-between">
                <Text as="p" variant="bodySm" tone="subdued">
                  {sceneAssetFile ? `Selected: ${sceneAssetFile.name}` : 'No file selected'}
                </Text>
                <Button
                  size="slim"
                  onClick={async () => {
                    setActionError('');
                    setActionSuccess('');
                    try {
                      const url = await uploadAssetFile('scene', sceneAssetFile);
                      setNewGenerationSceneData((previous) => ({ ...previous, scene_url: url }));
                      setSceneAssetFile(null);
                      setActionSuccess('Scene image uploaded successfully');
                    } catch (error) {
                      setActionError(error?.message || 'Failed to upload scene image');
                    }
                  }}
                  loading={isUploadingSceneAsset}
                  disabled={!sceneAssetFile || isUploadingMaskAsset || isCreatingGenerationScene}
                >
                  Upload Scene Image
                </Button>
              </InlineStack>
            </BlockStack>
          </div>
        )}
        {(getSceneTypeFields(newGenerationSceneData.type).showPrompt || (isPromptStory && newGenerationSceneData.type !== 'back')) && (
          <TextField
            label="Scene Prompt"
            value={newGenerationSceneData.scene_prompt}
            onChange={(value) => setNewGenerationSceneData(prev => ({ ...prev, scene_prompt: value }))}
            placeholder="Prompt used to generate this scene..."
            multiline={4}
            helpText="Optional. Stored as multiline string in database."
          />
        )}
        {newGenerationSceneData.type === 'image' ? (
          <Checkbox
            label="Enable text overlay"
            checked={newGenerationSceneData.text_overlay_enabled}
            onChange={(checked) => setNewGenerationSceneData(prev => ({ ...prev, text_overlay_enabled: checked }))}
          />
        ) : null}
        {isSpreadStory && newGenerationSceneData.type === 'image' && newGenerationSceneData.text_overlay_enabled ? (
          <BlockStack gap="200">
            <TextField
              label="Left Page Text"
              value={newGenerationSceneData.left_text || ''}
              onChange={(value) => setNewGenerationSceneData(prev => ({ ...prev, left_text: value }))}
              placeholder="Text drawn on the left page..."
              multiline={2}
            />
            <TextField
              label="Right Page Text"
              value={newGenerationSceneData.right_text || ''}
              onChange={(value) => setNewGenerationSceneData(prev => ({ ...prev, right_text: value }))}
              placeholder="Text drawn on the right page..."
              multiline={2}
            />
            <Text variant="bodySm" tone="subdued">
              At least one page text is required. Use {'{{Kid_Name}}'} for the kid name. Style and placement apply per page.
            </Text>
          </BlockStack>
        ) : null}
        {isSpreadStory && newGenerationSceneData.type === 'cover' && String(newGenerationSceneData.order) === '0' ? (
          <Checkbox
            label="Cover wrap (left half = back cover, right half = front cover)"
            checked={newGenerationSceneData.cover_wrap === true}
            onChange={(checked) => setNewGenerationSceneData(prev => ({ ...prev, cover_wrap: checked }))}
          />
        ) : null}
        {(getSceneTypeFields(newGenerationSceneData.type).showText ||
          (newGenerationSceneData.type === 'image' && newGenerationSceneData.text_overlay_enabled)) && (
          <div>
            {!(isSpreadStory && newGenerationSceneData.type === 'image') ? (
            <TextField
              label="Scene Text"
              value={newGenerationSceneData.text}
              onChange={(value) => setNewGenerationSceneData(prev => ({ ...prev, text: value }))}
              placeholder="Scene text..."
              multiline={2}
              required={getSceneTypeFields(newGenerationSceneData.type).textRequired}
            />
            ) : null}
            {['text', 'image'].includes(newGenerationSceneData.type) && !(isSpreadStory && newGenerationSceneData.type === 'image') ? (
              <Text variant="bodySm" tone="subdued" style={{ marginTop: '4px' }}>
                Use {'{{Kid_Name}}'} for Kid name variable
              </Text>
            ) : null}

            {newGenerationSceneData.type === 'cover' ? (
              <Text variant="bodySm" tone="subdued" style={{ marginTop: '4px' }}>
                Cover generation overlays the actual kid name. Scene text is kept for metadata only.
              </Text>
            ) : null}

            {['text', 'image'].includes(newGenerationSceneData.type) ? (
              <div style={{ marginTop: 12, border: '1px solid #e3e3e3', borderRadius: 8, padding: 12 }}>
                <BlockStack gap="300">
                  <Checkbox
                    label="Use story default text style"
                    checked={newGenerationSceneData.use_default_text_style}
                    onChange={(checked) => setNewGenerationSceneData(prev => ({ ...prev, use_default_text_style: checked }))}
                  />

                  {!newGenerationSceneData.use_default_text_style ? (
                    <TextStyleEditor
                      value={newGenerationSceneData.text_style_form}
                      onChange={(textStyle) => setNewGenerationSceneData((previous) => ({
                        ...previous,
                        text_style_form: textStyle,
                      }))}
                      fontOptions={fontOptions}
                      isLoadingFonts={isLoadingFonts}
                    />
                  ) : null}
                </BlockStack>
              </div>
            ) : null}

            {newGenerationSceneData.type === 'cover' ? (
              <div style={{ marginTop: 12, border: '1px solid #e3e3e3', borderRadius: 8, padding: 12 }}>
                <BlockStack gap="300">
                  <Checkbox
                    label="Use story default cover style"
                    checked={newGenerationSceneData.use_default_cover_style}
                    onChange={(checked) => setNewGenerationSceneData(prev => ({ ...prev, use_default_cover_style: checked }))}
                  />

                  {!newGenerationSceneData.use_default_cover_style ? (
                    <>
                      <Select
                        label="Font"
                        options={fontOptions}
                        value={newGenerationSceneData.cover_font_key}
                        onChange={(value) => setNewGenerationSceneData(prev => ({ ...prev, cover_font_key: value }))}
                        helpText={isLoadingFonts ? 'Loading font library...' : 'Optional. If empty, renderer uses language fallback font.'}
                      />

                      <InlineStack gap="300" wrap>
                        <div style={{ minWidth: 180, flex: 1 }}>
                          <TextField
                            label="Font Size"
                            type="number"
                            value={String(newGenerationSceneData.cover_font_size)}
                            onChange={(value) => setNewGenerationSceneData(prev => ({ ...prev, cover_font_size: value }))}
                            autoComplete="off"
                            placeholder="72"
                          />
                        </div>
                        <div style={{ minWidth: 180, flex: 1 }}>
                          <Select
                            label="Font Style"
                            options={FONT_STYLE_OPTIONS}
                            value={newGenerationSceneData.cover_font_style}
                            onChange={(value) => setNewGenerationSceneData(prev => ({ ...prev, cover_font_style: value }))}
                          />
                        </div>
                        <div style={{ minWidth: 180, flex: 1 }}>
                          <Select
                            label="Text Align"
                            options={TEXT_ALIGN_OPTIONS}
                            value={newGenerationSceneData.cover_text_align}
                            onChange={(value) => setNewGenerationSceneData(prev => ({ ...prev, cover_text_align: value }))}
                          />
                        </div>
                      </InlineStack>

                      <InlineStack gap="300" wrap>
                        <div style={{ minWidth: 180, flex: 1 }}>
                          <TextField
                            label="Text X"
                            type="number"
                            value={String(newGenerationSceneData.cover_text_x)}
                            onChange={(value) => setNewGenerationSceneData(prev => ({ ...prev, cover_text_x: value }))}
                            autoComplete="off"
                            placeholder="865"
                          />
                        </div>
                        <div style={{ minWidth: 180, flex: 1 }}>
                          <TextField
                            label="Text Y"
                            type="number"
                            value={String(newGenerationSceneData.cover_text_y)}
                            onChange={(value) => setNewGenerationSceneData(prev => ({ ...prev, cover_text_y: value }))}
                            autoComplete="off"
                            placeholder="2215"
                          />
                        </div>
                      </InlineStack>

                      <InlineStack gap="300" wrap>
                        <div style={{ minWidth: 180, flex: 1 }}>
                          <TextField
                            label="Font Color"
                            value={String(newGenerationSceneData.cover_font_color)}
                            onChange={(value) => setNewGenerationSceneData(prev => ({ ...prev, cover_font_color: value }))}
                            autoComplete="off"
                            placeholder="#ffffff"
                          />
                        </div>
                        <div style={{ minWidth: 180, flex: 1 }}>
                          <TextField
                            label="Rotation Angle"
                            type="number"
                            value={String(newGenerationSceneData.cover_rotation)}
                            onChange={(value) => setNewGenerationSceneData(prev => ({ ...prev, cover_rotation: value }))}
                            autoComplete="off"
                            placeholder="0"
                          />
                        </div>
                      </InlineStack>
                    </>
                  ) : null}
                </BlockStack>
              </div>
            ) : null}
          </div>
        )}
        {getSceneTypeFields(newGenerationSceneData.type).showMaskUrl && !isPromptStory && (
          <>
            <TextField
              label="Mask URL (or upload below)"
              value={newGenerationSceneData.mask_url}
              onChange={(value) => {
                setNewGenerationSceneData(prev => ({ ...prev, mask_url: value }));
                if (value) setMaskAssetFile(null);
              }}
              placeholder="https://example.com/mask.png"
              required={!maskAssetFile}
            />
            <div style={{ border: '1px solid #e3e3e3', borderRadius: 8, padding: 12 }}>
              <BlockStack gap="200">
                <Text as="p" variant="bodySm" tone="subdued">
                  Upload a mask image instead of entering a URL. It will also upload automatically when you create the scene.
                </Text>
                <label htmlFor="create-generation-mask-image">Mask image file</label>
                <input
                  id="create-generation-mask-image"
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={(event) => {
                    const file = event.target.files?.[0] || null;
                    setMaskAssetFile(file);
                    if (file) {
                      setNewGenerationSceneData((previous) => ({ ...previous, mask_url: '' }));
                    }
                    setActionError('');
                    setActionSuccess('');
                  }}
                  style={fileInputStyle}
                />
                <InlineStack gap="200" align="space-between">
                  <Text as="p" variant="bodySm" tone="subdued">
                    {maskAssetFile ? `Selected: ${maskAssetFile.name}` : 'No file selected'}
                  </Text>
                  <Button
                    size="slim"
                    onClick={async () => {
                      setActionError('');
                      setActionSuccess('');
                      try {
                        const url = await uploadAssetFile('mask', maskAssetFile);
                        setNewGenerationSceneData((previous) => ({ ...previous, mask_url: url }));
                        setMaskAssetFile(null);
                        setActionSuccess('Mask image uploaded successfully');
                      } catch (error) {
                        setActionError(error?.message || 'Failed to upload mask image');
                      }
                    }}
                    loading={isUploadingMaskAsset}
                    disabled={!maskAssetFile || isUploadingSceneAsset || isCreatingGenerationScene}
                  >
                    Upload Mask Image
                  </Button>
                </InlineStack>
              </BlockStack>
            </div>
          </>
        )}
        {getSceneTypeFields(newGenerationSceneData.type).showBlurred && (
          <Checkbox
            label="Blurred"
            checked={newGenerationSceneData.blurred}
            onChange={(checked) => setNewGenerationSceneData(prev => ({ ...prev, blurred: checked }))}
            required
          />
        )}
        {getSceneTypeFields(newGenerationSceneData.type).showDemoText && (
          <TextField
            label="Demo Text"
            value={newGenerationSceneData.demo_text}
            onChange={(value) => setNewGenerationSceneData(prev => ({ ...prev, demo_text: value }))}
            placeholder="Demo text for this scene"
            required={getSceneTypeFields(newGenerationSceneData.type).demoTextRequired}
          />
        )}
      </BlockStack>
    </Modal>
  );
};

export default CreateGenerationSceneModal; 
